import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog";
import { Zap, ArrowRight, ShieldCheck, X, Coins, Loader2 } from "lucide-react";

const TIER_DETAILS = {
    starter: {
        name: "Starter",
        price: 49,
        headline: "Unlock the AI features",
        pitch: "The full AI toolkit — Weekly Digest, Content Brief, Rank Drop Explainer, and cannibalization detector.",
        wins: [
            "100 credits / month",
            "3 projects · 100 keywords",
            "All AI features unlocked",
            "SERP-seeded Content Brief Generator",
            "Buy credit packs anytime you need more",
        ],
        lookup: "starter_monthly",
    },
    pro: {
        name: "Pro",
        price: 99,
        badge: "3-day free trial",
        headline: "Ship agency-grade output",
        pitch: "For growing operators. Historical trends, bulk portfolio diagnosis, and full white-label for client-facing reports.",
        wins: [
            "500 credits / month (5x Starter)",
            "10 projects · 500 keywords",
            "Bulk Diagnose (portfolio-wide)",
            "Historical rank tracking (14-day trend)",
            "White-label reports · 3 team seats",
        ],
        lookup: "pro_monthly",
    },
    agency: {
        name: "Agency",
        price: 299,
        headline: "The full-stack agency plan",
        pitch: "2,000 credits/mo, real backlink data, toxic link disavow, and everything you need to run an SEO agency.",
        wins: [
            "2,000 credits / month",
            "Unlimited projects · 2,500 keywords",
            "Backlinks + Referring Domains (live)",
            "Toxic Link Disavow + Export History",
            "10 team seats · Priority chat support",
        ],
        lookup: "agency_monthly",
    },
};

/**
 * Global modal triggered by axios 402 responses. Compares the user's current
 * tier to the recommended next-tier and CTA's straight to /pricing.
 */
export function UpgradeNagModal() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [ctx, setCtx] = useState({ message: "", target: "starter" });
    const [packLoading, setPackLoading] = useState(null);
    const isCreditError = /credit/i.test(ctx.message || "");

    useEffect(() => {
        const handler = (e) => {
            const detail = e.detail || {};
            // Pick a smarter target: never suggest their current tier
            let target = detail.target || "starter";
            const current = user?.tier || "free";
            if (current === "starter" && target === "starter") target = "pro";
            if (current === "pro" && target !== "agency") target = "agency";
            setCtx({ message: detail.message || "This feature requires a paid plan.", target });
            setOpen(true);
        };
        window.addEventListener("rf_upgrade_nag", handler);
        return () => window.removeEventListener("rf_upgrade_nag", handler);
    }, [user?.tier]);

    // Never nag admins / agency (they can't upgrade further)
    if (user?.is_admin || user?.tier === "agency") return null;

    const target = TIER_DETAILS[ctx.target] || TIER_DETAILS.starter;

    const goPricing = () => {
        setOpen(false);
        nav("/pricing");
    };

    const buyPack = async (lookup_key) => {
        setPackLoading(lookup_key);
        try {
            const r = await client.post("/billing/checkout", {
                lookup_key,
                origin_url: window.location.origin,
            });
            if (r.data?.checkout_url) window.location.href = r.data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout");
        } finally {
            setPackLoading(null);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                data-testid="upgrade-nag-modal"
                className="max-w-lg bg-background border border-primary/40 p-0 overflow-hidden"
            >
                <div className="bg-primary/10 border-b border-primary/30 px-6 py-4 flex items-center gap-3">
                    <div className="w-9 h-9 border border-primary/60 bg-primary/10 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1">
                        <div className="overline text-primary">LIMIT REACHED</div>
                        <div className="text-sm font-mono text-muted-foreground mt-0.5" data-testid="nag-message">{ctx.message}</div>
                    </div>
                    <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground p-1" aria-label="Close">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="px-6 py-6">
                    <DialogHeader>
                        <div className="flex items-center gap-2">
                            <DialogTitle className="font-display text-3xl tracking-tight">
                                Upgrade to <span className="text-primary">{target.name}</span>
                            </DialogTitle>
                            {target.badge && (
                                <span className="text-[10px] font-mono uppercase tracking-widest border border-primary/60 text-primary px-2 py-0.5">
                                    {target.badge}
                                </span>
                            )}
                        </div>
                        <DialogDescription className="text-muted-foreground">
                            {target.pitch}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-5 flex items-baseline gap-2">
                        <div className="font-display text-5xl">${target.price}</div>
                        <div className="text-muted-foreground">/ month</div>
                        <div className="ml-auto text-xs text-muted-foreground">or save 20% yearly</div>
                    </div>

                    <div className="mt-6 border border-border p-4 space-y-2" data-testid="nag-wins">
                        {target.wins.map((w) => (
                            <div key={w} className="text-sm flex items-start gap-2">
                                <ShieldCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                <span>{w}</span>
                            </div>
                        ))}
                    </div>

                    {/* Overage credit packs — shown when the user has a paid tier but hit their credit cap */}
                    {isCreditError && user?.tier && user.tier !== "free" && (
                        <div className="mt-5 border border-primary/30 bg-primary/5 p-4" data-testid="nag-credit-packs">
                            <div className="overline flex items-center gap-2 mb-2 text-primary">
                                <Coins className="w-3 h-3" /> OR JUST TOP UP
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                                Not ready to upgrade? Buy overage credits for this month — no plan change, never expires.
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    data-testid="nag-buy-pack-100"
                                    onClick={() => buyPack("credit_pack_100")}
                                    disabled={packLoading !== null}
                                    className="border border-primary/60 text-primary hover:bg-primary/10 py-2.5 px-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {packLoading === "credit_pack_100" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
                                    +100 credits · $10
                                </button>
                                <button
                                    data-testid="nag-buy-pack-500"
                                    onClick={() => buyPack("credit_pack_500")}
                                    disabled={packLoading !== null}
                                    className="border border-primary/60 text-primary hover:bg-primary/10 py-2.5 px-3 text-sm flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {packLoading === "credit_pack_500" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
                                    +500 credits · $40
                                </button>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-2 text-center">500-pack saves 20% vs the 100-pack</div>
                        </div>
                    )}

                    <div className="mt-6 flex gap-3">
                        <button
                            data-testid="nag-upgrade-btn"
                            onClick={goPricing}
                            className="flex-1 bg-primary text-primary-foreground px-4 py-3 text-sm font-medium hover:bg-primary/90 flex items-center justify-center gap-2"
                        >
                            {target.badge ? "Start free trial" : `Upgrade to ${target.name}`} <ArrowRight className="w-4 h-4" />
                        </button>
                        <button
                            data-testid="nag-dismiss-btn"
                            onClick={() => setOpen(false)}
                            className="border border-border px-4 py-3 text-sm text-muted-foreground hover:text-foreground"
                        >
                            Not now
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
